/**
 * Admin: Processing Run History
 *
 * Shows a paginated table of every queue-based processing run with:
 *   - Entity name, timestamp, duration, extraction method
 *   - Event stats: found, new, exact duplicates, vector matches, errors
 *   - Infrastructure stats: screenshots stored, tokens used
 *   - Estimated LLM cost (based on Microsoft public pricing)
 *   - Aggregate summary row + pricing info footer
 *
 * Backend: GET /api/workflowMetadata?action=runs
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { RefreshCw, Info } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import {
  getProcessingRuns,
  type ProcessingRun,
  type ProcessingRunsResponse,
} from '../services/adminApi';
import {
  Card,
  CardTitle,
  Banner,
  PrimaryButton,
  SecondaryButton,
  StatGrid,
  Metric,
  ButtonRow,
} from '../components/admin/AdminUI';
import { colors, spacing, typography, borderRadius, shadows } from '../styles/theme';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  align-items: flex-end;
  margin-bottom: ${spacing[4]};
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  min-width: 160px;
`;

const FilterLabel = styled.label`
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FilterInput = styled.input`
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};
  background: ${colors.white};

  &:focus {
    outline: none;
    border-color: ${colors.primary[400]};
    box-shadow: 0 0 0 2px ${colors.primary[100]};
  }
`;

const FilterSelect = styled.select`
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};
  background: ${colors.white};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.primary[400]};
    box-shadow: 0 0 0 2px ${colors.primary[100]};
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.border.light};
  box-shadow: ${shadows.sm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${typography.fontSizes.sm};
  background: ${colors.white};
`;

const Th = styled.th<{ align?: string }>`
  padding: ${spacing[2]} ${spacing[2]};
  background: ${colors.gray[50]};
  text-align: ${p => p.align || 'left'};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.secondary};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid ${colors.border.light};
  white-space: nowrap;
`;

const Td = styled.td<{ align?: string; muted?: boolean }>`
  padding: ${spacing[2]} ${spacing[2]};
  border-bottom: 1px solid ${colors.border.light};
  text-align: ${p => p.align || 'left'};
  color: ${p => p.muted ? colors.text.tertiary : colors.text.primary};
  vertical-align: top;
  white-space: nowrap;
  font-size: 13px;
`;

const TrSummary = styled.tr`
  background: ${colors.primary[50]};
  font-weight: ${typography.fontWeights.semibold};

  td {
    color: ${colors.primary[700]};
    border-top: 2px solid ${colors.primary[200]};
    border-bottom: 2px solid ${colors.primary[200]};
  }
`;

const StatusBadge = styled.span<{ variant: 'success' | 'error' | 'warn' | 'info' }>`
  display: inline-block;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.md};
  font-size: 11px;
  font-weight: ${typography.fontWeights.medium};
  background: ${p =>
    p.variant === 'success' ? colors.success[50] :
    p.variant === 'error'   ? colors.error[50] :
    p.variant === 'warn'    ? colors.warning[50] :
    colors.gray[100]};
  color: ${p =>
    p.variant === 'success' ? colors.success[700] :
    p.variant === 'error'   ? colors.error[700] :
    p.variant === 'warn'    ? colors.warning[700] :
    colors.text.secondary};
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MethodBadge = styled.span<{ method: string }>`
  display: inline-block;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  background: ${p =>
    p.method === 'markdown'   ? colors.secondary[50] :
    p.method === 'screenshot' ? colors.primary[50] :
    colors.gray[100]};
  color: ${p =>
    p.method === 'markdown'   ? colors.secondary[700] :
    p.method === 'screenshot' ? colors.primary[700] :
    colors.text.secondary};
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  padding: ${spacing[3]} 0;
  flex-wrap: wrap;
`;

const PaginationInfo = styled.span`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
`;

const PricingNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  background: ${colors.gray[50]};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.light};
  margin-top: ${spacing[4]};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dash(value: number | null | undefined): string {
  if (value == null) return '—';
  return String(value);
}

function currency(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value === 0) return '$0.00';
  if (value < 0.001) return `<$0.001`;
  return `$${value.toFixed(4)}`;
}

function duration(sec: number | null | undefined): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function classifyStatus(status: string | null | undefined): 'success' | 'error' | 'warn' | 'info' {
  if (!status) return 'info';
  const s = status.toLowerCase();
  if (s === 'success') return 'success';
  if (s.includes('fail') || s.includes('error')) return 'error';
  if (s.includes('no recent') || s.includes('no source')) return 'warn';
  return 'info';
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function localInputToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminProcessingRunsPage() {
  // Default: last 7 days
  const [startInput, setStartInput] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toLocalInput(d);
  });
  const [endInput, setEndInput] = useState(() => toLocalInput(new Date()));
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProcessingRunsResponse | null>(null);

  const load = useCallback(async (pg = page) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProcessingRuns({
        startTime: localInputToIso(startInput),
        endTime: localInputToIso(endInput),
        page: pg,
        pageSize: PAGE_SIZE,
        status: statusFilter || undefined,
      });
      setResult(data);
      setPage(pg);
    } catch (e: any) {
      setError(e?.message || 'Failed to load processing runs');
    } finally {
      setLoading(false);
    }
  }, [startInput, endInput, statusFilter, page]);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = result?.summary;
  const runs = result?.runs ?? [];
  const pricing = result?.pricingInfo;

  return (
    <AdminLayout
      currentPage="processingRuns"
      pageTitle="Processing Run History"
      pageDescription="Per-run stats for every queue-based website processing job: events found, LLM tokens consumed, and estimated cost."
    >
      {/* ── Summary metrics ── */}
      {summary && (
        <StatGrid style={{ marginBottom: spacing[6] }}>
          <Metric
            label="Total Runs"
            value={summary.totalRuns.toLocaleString()}
          />
          <Metric
            label="Successful"
            value={summary.successfulRuns.toLocaleString()}
            tone="success"
          />
          <Metric
            label="Failed"
            value={summary.failedRuns.toLocaleString()}
            tone={summary.failedRuns > 0 ? 'error' : 'neutral'}
          />
          <Metric
            label="New Events"
            value={summary.totalNewEvents.toLocaleString()}
            tone="success"
          />
          <Metric
            label="Screenshots"
            value={summary.totalScreenshots.toLocaleString()}
          />
          <Metric
            label="Total Tokens"
            value={summary.totalTokens > 0 ? summary.totalTokens.toLocaleString() : '—'}
          />
          <Metric
            label="Est. Cost (window)"
            value={currency(summary.totalEstimatedCostUsd)}
          />
          <Metric
            label="Avg Duration"
            value={summary.avgDurationSec != null ? duration(Math.round(summary.avgDurationSec)) : '—'}
          />
        </StatGrid>
      )}

      {/* ── Filters ── */}
      <Card style={{ marginBottom: spacing[4] }}>
        <CardTitle>Filters</CardTitle>
        <FiltersRow>
          <FilterField>
            <FilterLabel>From</FilterLabel>
            <FilterInput
              type="datetime-local"
              value={startInput}
              onChange={e => setStartInput(e.target.value)}
            />
          </FilterField>
          <FilterField>
            <FilterLabel>To</FilterLabel>
            <FilterInput
              type="datetime-local"
              value={endInput}
              onChange={e => setEndInput(e.target.value)}
            />
          </FilterField>
          <FilterField>
            <FilterLabel>Status filter</FilterLabel>
            <FilterSelect
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="success">Success</option>
              <option value="fail">Failed</option>
              <option value="error">Error</option>
              <option value="no recent">No recent events</option>
            </FilterSelect>
          </FilterField>
          <PrimaryButton onClick={() => load(1)} disabled={loading}>
            {loading ? <RefreshCw size={16} className="spin" /> : <RefreshCw size={16} />}
            {loading ? 'Loading…' : 'Load Runs'}
          </PrimaryButton>
        </FiltersRow>
      </Card>

      {/* ── Error ── */}
      {error && (
        <Banner tone="error" style={{ marginBottom: spacing[4] }}>
          {error}
        </Banner>
      )}

      {/* ── Table ── */}
      {result && (
        <>
          <PaginationRow>
            <PaginationInfo>
              {result.totalCount.toLocaleString()} run{result.totalCount !== 1 ? 's' : ''} found
              {result.totalPages > 1 && ` — page ${result.page} of ${result.totalPages}`}
            </PaginationInfo>
            <ButtonRow>
              <SecondaryButton
                onClick={() => load(page - 1)}
                disabled={loading || page <= 1}
              >
                ← Previous
              </SecondaryButton>
              <SecondaryButton
                onClick={() => load(page + 1)}
                disabled={loading || page >= result.totalPages}
              >
                Next →
              </SecondaryButton>
            </ButtonRow>
          </PaginationRow>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Entity</Th>
                  <Th>Started</Th>
                  <Th align="right">Dur</Th>
                  <Th>Method</Th>
                  <Th>Status</Th>
                  <Th align="right">Found</Th>
                  <Th align="right">New</Th>
                  <Th align="right">Dup</Th>
                  <Th align="right">Vec</Th>
                  <Th align="right">Err</Th>
                  <Th align="right">Shots</Th>
                  <Th align="right">Tokens</Th>
                  <Th align="right">Cost</Th>
                </tr>
              </thead>
              <tbody>
                {/* Summary row */}
                {summary && (
                  <TrSummary>
                    <Td><strong>Totals (window)</strong></Td>
                    <Td muted>—</Td>
                    <Td align="right" muted>
                      {summary.avgDurationSec != null ? `avg ${duration(Math.round(summary.avgDurationSec))}` : '—'}
                    </Td>
                    <Td muted>—</Td>
                    <Td>
                      <span style={{ color: colors.success[600] }}>{summary.successfulRuns} ok</span>
                      {summary.failedRuns > 0 && (
                        <span style={{ color: colors.error[600], marginLeft: spacing[2] }}>
                          {summary.failedRuns} failed
                        </span>
                      )}
                    </Td>
                    <Td align="right">{summary.totalEventsFound.toLocaleString()}</Td>
                    <Td align="right">{summary.totalNewEvents.toLocaleString()}</Td>
                    <Td align="right">{summary.totalExactDuplicates.toLocaleString()}</Td>
                    <Td align="right">{summary.totalVectorMatches.toLocaleString()}</Td>
                    <Td align="right" muted>—</Td>
                    <Td align="right">{summary.totalScreenshots.toLocaleString()}</Td>
                    <Td align="right">
                      {summary.totalTokens > 0 ? summary.totalTokens.toLocaleString() : '—'}
                    </Td>
                    <Td align="right">
                      <strong>{currency(summary.totalEstimatedCostUsd)}</strong>
                    </Td>
                  </TrSummary>
                )}

                {runs.length === 0 && (
                  <tr>
                    <Td colSpan={13} muted style={{ textAlign: 'center', padding: spacing[6] }}>
                      No runs found in the selected time range.
                    </Td>
                  </tr>
                )}

                {runs.map((run: ProcessingRun) => (
                  <tr key={run.id}>
                    <Td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${run.partnerName || 'Unknown'} (ID ${run.eventSourceId})`}>
                      <span style={{ fontWeight: typography.fontWeights.medium }}>
                        {run.partnerName || <span style={{ color: colors.text.tertiary }}>Unknown</span>}
                      </span>
                    </Td>
                    <Td title={run.source || undefined}>
                      {fmtDate(run.startTime)}
                    </Td>
                    <Td align="right">{duration(run.processingDurationSec)}</Td>
                    <Td>
                      {run.extractionMethod ? (
                        <MethodBadge method={run.extractionMethod}>{run.extractionMethod}</MethodBadge>
                      ) : <span style={{ color: colors.text.tertiary }}>—</span>}
                    </Td>
                    <Td>
                      {run.status ? (
                        <StatusBadge variant={classifyStatus(run.status)} title={run.status}>
                          {run.status}
                        </StatusBadge>
                      ) : '—'}
                    </Td>
                    <Td align="right">{dash(run.totalEventsFound)}</Td>
                    <Td align="right" style={{ color: run.totalNewEvents ? colors.success[600] : undefined }}>
                      {dash(run.totalNewEvents)}
                    </Td>
                    <Td align="right" muted>{dash(run.exactDuplicates)}</Td>
                    <Td align="right" muted>{dash(run.vectorMatches)}</Td>
                    <Td align="right" style={{ color: run.totalErrors ? colors.error[600] : undefined }}>
                      {dash(run.totalErrors)}
                    </Td>
                    <Td align="right" muted>{dash(run.screenshotsCount)}</Td>
                    <Td align="right" muted>
                      {run.totalTokens != null ? run.totalTokens.toLocaleString() : '—'}
                    </Td>
                    <Td align="right">
                      {run.estimatedCostUsd != null ? (
                        <span style={{ fontFamily: 'monospace' }}>{currency(run.estimatedCostUsd)}</span>
                      ) : '—'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          {/* ── Pagination bottom ── */}
          <PaginationRow style={{ marginTop: spacing[3] }}>
            <PaginationInfo>
              {result.totalCount.toLocaleString()} run{result.totalCount !== 1 ? 's' : ''}
              {result.totalPages > 1 && ` — page ${result.page} of ${result.totalPages}`}
            </PaginationInfo>
            <ButtonRow>
              <SecondaryButton
                onClick={() => load(page - 1)}
                disabled={loading || page <= 1}
              >
                ← Previous
              </SecondaryButton>
              <SecondaryButton
                onClick={() => load(page + 1)}
                disabled={loading || page >= result.totalPages}
              >
                Next →
              </SecondaryButton>
            </ButtonRow>
          </PaginationRow>

          {/* ── Pricing note ── */}
          {pricing && (
            <PricingNote>
              <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                {pricing.note}{' '}
                <a href={pricing.priceUrl} target="_blank" rel="noopener noreferrer"
                  style={{ color: colors.primary[600] }}>
                  View Azure pricing →
                </a>
                {' '}Rates: GPT-4o-mini ${pricing.models['gpt-4o-mini']?.inputPerMillion}/1M input,{' '}
                ${pricing.models['gpt-4o-mini']?.outputPerMillion}/1M output;{' '}
                GPT-4o ${pricing.models['gpt-4o']?.inputPerMillion}/1M input,{' '}
                ${pricing.models['gpt-4o']?.outputPerMillion}/1M output;{' '}
                text-embedding-3-large ${pricing.models['text-embedding-3-large']?.inputPerMillion}/1M tokens.{' '}
                Runs without token data show "—" — these predate the stats collection feature.
              </span>
            </PricingNote>
          )}
        </>
      )}
    </AdminLayout>
  );
}
